import { auth } from './firebase';
import { isLocalGuestSession } from './guestSession';

export async function getAuthHeaders(extraHeaders = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('NOT_AUTHENTICATED');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...extraHeaders,
  };
}

/** Pull a readable error string from a failed API response */
export async function readApiErrorMessage(response) {
  let bodyText = '';
  try {
    bodyText = await response.text();
  } catch (_) {
    return response.statusText || `Request failed (${response.status})`;
  }

  if (!bodyText.trim()) {
    return response.statusText || `Request failed (${response.status})`;
  }

  try {
    const parsed = JSON.parse(bodyText);
    if (typeof parsed.error === 'string' && parsed.error.trim()) return parsed.error.trim();
    if (parsed.error?.message) return String(parsed.error.message);
    if (parsed.message) return String(parsed.message);
  } catch (_) {
    /* plain text body */
  }

  return bodyText.trim().slice(0, 400);
}

/** Turn network / auth failures into plain English for toasts and chat */
export function humanizeClientError(err) {
  if (!err) return 'Something went wrong. Please try again.';
  if (err.name === 'AbortError') return 'Request cancelled.';

  const msg = (err.message || '').trim();
  if (msg === 'NOT_AUTHENTICATED') {
    return 'Please sign in or tap Continue as Guest to chat.';
  }

  const lower = msg.toLowerCase();
  if (lower.includes('failed to fetch') || err instanceof TypeError) {
    if (process.env.NODE_ENV === 'development') {
      return 'Cannot reach the chat server. Run npm start (dev server + proxy), not only npm run build.';
    }
    return 'Cannot reach the server. Check your connection and try again.';
  }

  if (/^invalid_request_error$/i.test(msg)) {
    return 'The request was invalid. Try a shorter message or switch to model v1.';
  }
  if (/^insufficient_quota$/i.test(msg)) {
    return 'The AI service is out of quota. Try again later.';
  }
  if (/^model_not_found$/i.test(msg) || /model not allowed/i.test(msg)) {
    return 'That model is not available. Switch to v1 (Faster) and try again.';
  }
  if (/^Email not verified$/i.test(msg)) {
    return 'Please verify your email first, or continue as Guest.';
  }
  if (/auth\/operation-not-allowed/i.test(msg) || /anonymous/i.test(lower)) {
    return 'Guest mode is available without signing in — try Continue as Guest again.';
  }

  return msg.replace(/^Error:\s*/i, '').replace(/^Firebase:\s*/i, '') || 'Something went wrong. Please try again.';
}

/** fetch() with Firebase token when available; local guest uses legacy open proxy */
export async function apiFetch(url, options = {}) {
  const user = auth.currentUser;
  const localGuest = isLocalGuestSession();

  if (!user && !localGuest) {
    throw new Error('NOT_AUTHENTICATED');
  }

  let headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (user) {
    try {
      const token = await user.getIdToken();
      headers = { ...headers, Authorization: `Bearer ${token}` };
    } catch (_) {
      /* legacy proxy may still accept the request */
    }
  }

  return fetch(url, {
    ...options,
    headers,
  });
}
